// Pipeline Command Centre — the redesigned Business Metrics view.
//
// One-page comprehensive report of the entire lead-to-settlement funnel:
//   • Period selector (day / week / month / quarter / year + custom range)
//   • Filter bar (broker / lender / credit-team / lead source / purpose /
//     application type / stage)
//   • Volumes strip — submitted, approved, settled $ with vs-previous-period deltas
//   • Approval / decline / withdrawal rate KPIs
//   • 8-stage funnel with conversion % between adjacent stages
//   • Bottleneck strip — median + p90 days at each stage, plus "currently
//     sitting here" count
//   • Lender leaderboard — approvals, avg turnaround (days), approval rate
//   • Credit team leaderboard — files handled, approval rate, settlement rate
//   • Broker leaderboard — submitted / approved / settled volumes
//   • Distribution — lead source / loan purpose / application type / loan type
//   • Reasons — top decline / withdrawal / hold reasons
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, Filter, Calendar, TrendingUp, TrendingDown,
  Landmark, Users, UserCog, AlertTriangle, RefreshCw,
  ArrowRight, Timer, XCircle, PauseCircle, CheckCircle2,
  ChevronDown, ChevronRight, FileDown,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { backendUrl } from './gameplanUtils';
import {
  LENDERS, CREDIT_TEAM, LOAN_PURPOSES, APPLICATION_TYPES,
  PIPELINE_STAGES, TERMINAL_STAGES,
} from './pipelineConstants';
import { exportPipelinePdf } from './pipelinePdfExport';

const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;
const fmtNum   = (n) => Number(n || 0).toLocaleString('en-AU');
const fmtPct   = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const fmtDays  = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}d`);

const delta = (curr, prev) => {
  const c = Number(curr) || 0;
  const p = Number(prev) || 0;
  if (p === 0) return c === 0 ? { pct: 0, dir: 'flat' } : { pct: 100, dir: 'up' };
  const pct = ((c - p) / Math.abs(p)) * 100;
  return { pct: Math.abs(pct), dir: pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'flat' };
};

// ─────────────────────────────────────────────────────────────
// Reusable primitives
// ─────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, prev, positive = true, tone = 'slate', testid }) => {
  const d = delta(value, prev);
  const good = (positive && d.dir === 'up') || (!positive && d.dir === 'down');
  return (
    <div className="rounded-2xl bg-white border border-slate-200/70 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid={testid}>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-black tabular-nums text-${tone}-900`}>{value}</p>
      <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold ${good ? 'text-emerald-600' : d.dir === 'flat' ? 'text-slate-400' : 'text-rose-600'}`}>
        {d.dir === 'up' && <TrendingUp className="w-3 h-3" />}
        {d.dir === 'down' && <TrendingDown className="w-3 h-3" />}
        {d.dir === 'flat' ? '—' : `${d.pct.toFixed(1)}% vs prev`}
      </div>
    </div>
  );
};

const Panel = ({ title, icon: Icon, children, testid, className = '' }) => (
  <section className={`rounded-2xl bg-white border border-slate-200/70 p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-slate-600" strokeWidth={2.2} />}
      <h3 className="text-sm font-black uppercase tracking-widest text-slate-900">{title}</h3>
    </div>
    {children}
  </section>
);

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
const PipelineCommandCentre = ({ authToken, options, refreshSignal }) => {
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [openBuckets, setOpenBuckets] = useState({});
  const [filters, setFilters] = useState({
    broker: '', lender: '', credit_team_member: '', lead_source: '',
    loan_purpose: '', application_type: '', loan_type: '', stage: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') { params.set('start', customStart); params.set('end', customEnd); }
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${backendUrl}/api/gameplan/pipeline-analytics?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (r.ok) setData(await r.json());
    } catch (_) { /* silent */ }
    setLoading(false);
  }, [authToken, period, customStart, customEnd, filters]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const clearFilters = () => setFilters({
    broker: '', lender: '', credit_team_member: '', lead_source: '',
    loan_purpose: '', application_type: '', loan_type: '', stage: '',
  });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const current = data?.current;
  const previous = data?.previous;
  const maxFunnel = useMemo(() => {
    if (!current) return 1;
    return Math.max(1, ...PIPELINE_STAGES.map(s => current.counts[s.key] || 0));
  }, [current]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="pipeline-command-centre">
      {/* Period + Filter toolbar */}
      <div className="rounded-2xl bg-white border border-slate-200/70 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="pipeline-period-selector">
            {['today','week','month','quarter','year','custom'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                data-testid={`period-${p}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${period === p ? 'bg-slate-950 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}>
                {p}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-1">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid="period-custom-start"/>
              <ArrowRight className="w-3 h-3 text-slate-400" />
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" data-testid="period-custom-end"/>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="text-[11px] font-bold uppercase tracking-wider text-rose-600 hover:text-rose-800" data-testid="pipeline-clear-filters">
                Clear {activeFilterCount}
              </button>
            )}
            <button
              onClick={() => exportPipelinePdf({ data: { current, previous }, period, customStart, customEnd, filters })}
              disabled={!current}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="pipeline-export-pdf"
              title="Download the on-screen pipeline analytics as a formatted PDF report"
            >
              <FileDown className="w-3.5 h-3.5" />
              Save PDF
            </button>
            <button onClick={load} className="rounded-lg p-1.5 hover:bg-slate-100" title="Refresh" data-testid="pipeline-refresh">
              <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filter row — collapsible in the future; for now shown inline */}
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {[
            ['broker', 'Broker', (options?.brokers || []).map(b => b.name)],
            ['lender', 'Lender', LENDERS],
            ['credit_team_member', 'Credit team', CREDIT_TEAM],
            ['lead_source', 'Lead source', options?.leadSources || []],
            ['loan_purpose', 'Loan purpose', LOAN_PURPOSES],
            ['application_type', 'App type', APPLICATION_TYPES],
            ['loan_type', 'Loan type', ['Purchase', 'Refinance', 'Investment', 'Construction', 'Bridging']],
            ['stage', 'Stage', [...PIPELINE_STAGES.map(s => s.key), ...TERMINAL_STAGES.map(s => s.key)]],
          ].map(([key, label, opts]) => (
            <Select key={key} value={filters[key] || '__all__'} onValueChange={(v) => setFilters({ ...filters, [key]: v === '__all__' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`filter-${key}`}>
                <Filter className="w-3 h-3 mr-1 text-slate-400" />
                <SelectValue placeholder={label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All {label}s</SelectItem>
                {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
        </div>
      </div>

      {!current ? (
        <div className="p-8 text-center text-sm text-slate-500">Loading pipeline data…</div>
      ) : (
        <>
          {/* Volumes strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard label="Submitted $" value={fmtMoney(current.volumes.submitted_amount)} prev={previous?.volumes?.submitted_amount} testid="kpi-submitted-amount"/>
            <KpiCard label="Approved $"  value={fmtMoney(current.volumes.approved_amount)}  prev={previous?.volumes?.approved_amount}  testid="kpi-approved-amount"/>
            <KpiCard label="Settled $"   value={fmtMoney(current.volumes.settled_amount)}   prev={previous?.volumes?.settled_amount}   testid="kpi-settled-amount"/>
            <KpiCard label="Approval rate"    value={fmtPct(current.volumes.approval_rate)}    prev={previous?.volumes?.approval_rate}    testid="kpi-approval-rate"/>
            <KpiCard label="Decline rate"     value={fmtPct(current.volumes.decline_rate)}     prev={previous?.volumes?.decline_rate}     positive={false} testid="kpi-decline-rate"/>
            <KpiCard label="Avg loan size"    value={fmtMoney(current.volumes.avg_loan_size)}  prev={previous?.volumes?.avg_loan_size}    testid="kpi-avg-loan-size"/>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* 8-stage funnel */}
            <Panel title="Client Journey" icon={BarChart3} testid="pipeline-funnel" className="lg:col-span-2">
              <div className="space-y-2">
                {PIPELINE_STAGES.map((s, i) => {
                  const cnt = current.counts[s.key] || 0;
                  const width = Math.max(4, (cnt / maxFunnel) * 100);
                  const conv = current.conversions[i - 1];
                  return (
                    <div key={s.key}>
                      {conv && (
                        <div className="pl-3 my-0.5 flex items-center gap-1 text-[10px] font-bold text-slate-400">
                          <ArrowRight className="w-2.5 h-2.5" />
                          <span>{fmtPct(conv.rate)} progress from prior stage</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3" data-testid={`funnel-${s.key}`}>
                        <div className="w-40 shrink-0 text-xs font-bold text-slate-700">{s.label}</div>
                        <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full rounded-md flex items-center px-2 text-[11px] font-black text-white transition-all"
                               style={{ width: `${width}%`, background: s.tone }}>
                            {cnt > 0 && cnt}
                          </div>
                        </div>
                        <div className="w-14 text-right text-sm font-black tabular-nums text-slate-900">{fmtNum(cnt)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Terminal states */}
              <div className="mt-4 grid grid-cols-3 gap-2">
                {TERMINAL_STAGES.map(s => (
                  <div key={s.key} className="rounded-lg p-3 flex items-center gap-2" style={{ background: `${s.tone}15`, borderLeft: `3px solid ${s.tone}` }} data-testid={`terminal-${s.key}`}>
                    {s.key === 'declined' && <XCircle className="w-4 h-4" style={{ color: s.tone }} />}
                    {s.key === 'withdrawn' && <TrendingDown className="w-4 h-4" style={{ color: s.tone }} />}
                    {s.key === 'on_hold' && <PauseCircle className="w-4 h-4" style={{ color: s.tone }} />}
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: s.tone }}>{s.label}</p>
                      <p className="text-lg font-black tabular-nums text-slate-900">{fmtNum(current.counts[s.key] || 0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            {/* Bottlenecks — median/p90 days per stage + currently sitting */}
            <Panel title="Bottlenecks · time-at-stage" icon={Timer} testid="pipeline-bottlenecks">
              <div className="space-y-2">
                {current.bottlenecks.filter(b => PIPELINE_STAGES.find(s => s.key === b.stage)).map((b) => {
                  const stageMeta = PIPELINE_STAGES.find(s => s.key === b.stage);
                  const slow = (b.median_days ?? 0) > 7;
                  return (
                    <div key={b.stage} className="flex items-center justify-between text-xs" data-testid={`bn-${b.stage}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-700 truncate">{stageMeta?.label}</p>
                        <p className="text-[10px] text-slate-400">now: {b.currently_at_stage} deal{b.currently_at_stage === 1 ? '' : 's'}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-black tabular-nums ${slow ? 'text-rose-600' : 'text-slate-900'}`}>{fmtDays(b.median_days)}</p>
                        <p className="text-[10px] text-slate-400 tabular-nums">p90 {fmtDays(b.p90_days)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Lender leaderboard — deep detail */}
            <Panel title="Lender leaderboard · turnaround detail" icon={Landmark} testid="pipeline-lenders">
              {current.lenders.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No lender data yet — add &ldquo;Lender&rdquo; when creating deals.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[9.5px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left py-1 pr-2">Bank</th>
                        <th className="text-right px-1">Files</th>
                        <th className="text-right px-1">Open</th>
                        <th className="text-right px-1 text-emerald-700">Appr</th>
                        <th className="text-right px-1 text-rose-600">Dec</th>
                        <th className="text-right px-1">Set</th>
                        <th className="text-right px-1" title="Median days: submitted → unconditional approval">TAT&nbsp;med</th>
                        <th className="text-right px-1" title="90th percentile (worst-case) turnaround">p90</th>
                        <th className="text-right px-1" title="Median days: submitted → conditional">→Cond</th>
                        <th className="text-right px-1" title="Median days: conditional → unconditional">Cond→U</th>
                        <th className="text-right pl-1">Sub $</th>
                        <th className="text-right pl-1 text-emerald-700">Set $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.lenders.map((l) => (
                        <tr key={l.lender} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`lender-${l.lender}`}>
                          <td className="py-1.5 pr-2 font-bold text-slate-900 whitespace-nowrap">{l.lender}</td>
                          <td className="text-right px-1 tabular-nums">{l.count}</td>
                          <td className="text-right px-1 tabular-nums text-slate-600">{l.open}</td>
                          <td className="text-right px-1 tabular-nums font-bold text-emerald-700">{l.approvals}</td>
                          <td className="text-right px-1 tabular-nums text-rose-600">{l.declines}</td>
                          <td className="text-right px-1 tabular-nums text-sky-700">{l.settled}</td>
                          <td className="text-right px-1 tabular-nums">{fmtDays(l.median_turnaround_days)}</td>
                          <td className="text-right px-1 tabular-nums text-slate-400">{fmtDays(l.p90_turnaround_days)}</td>
                          <td className="text-right px-1 tabular-nums text-slate-500">{fmtDays(l.median_sub_to_cond)}</td>
                          <td className="text-right px-1 tabular-nums text-slate-500">{fmtDays(l.median_cond_to_uncond)}</td>
                          <td className="text-right pl-1 tabular-nums text-slate-600">{fmtMoney(l.submitted_amount)}</td>
                          <td className="text-right pl-1 tabular-nums font-bold text-emerald-700">{fmtMoney(l.settled_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* Credit team leaderboard — deep detail */}
            <Panel title="Credit team · assessor detail" icon={UserCog} testid="pipeline-credit-team">
              {current.credit_team.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No credit officer assigned yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[9.5px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left py-1 pr-2">Officer</th>
                        <th className="text-right px-1">Files</th>
                        <th className="text-right px-1">Open</th>
                        <th className="text-right px-1 text-emerald-700">Appr</th>
                        <th className="text-right px-1">Set</th>
                        <th className="text-right px-1 text-rose-600">Dec</th>
                        <th className="text-right px-1 text-amber-700">Hold</th>
                        <th className="text-right px-1" title="Median days: submitted → unconditional approval">TAT&nbsp;med</th>
                        <th className="text-right px-1" title="90th percentile turnaround">p90</th>
                        <th className="text-right px-1">Appr %</th>
                        <th className="text-right pl-1">Sub $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.credit_team.map((m) => (
                        <tr key={m.member} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`credit-${m.member}`}>
                          <td className="py-1.5 pr-2 font-bold text-slate-900 whitespace-nowrap">{m.member}</td>
                          <td className="text-right px-1 tabular-nums">{m.count}</td>
                          <td className="text-right px-1 tabular-nums text-slate-600">{m.open}</td>
                          <td className="text-right px-1 tabular-nums font-bold text-emerald-700">{m.approvals}</td>
                          <td className="text-right px-1 tabular-nums text-sky-700">{m.settled}</td>
                          <td className="text-right px-1 tabular-nums text-rose-600">{m.declines}</td>
                          <td className="text-right px-1 tabular-nums text-amber-700">{m.on_hold}</td>
                          <td className="text-right px-1 tabular-nums">{fmtDays(m.median_turnaround_days)}</td>
                          <td className="text-right px-1 tabular-nums text-slate-400">{fmtDays(m.p90_turnaround_days)}</td>
                          <td className="text-right px-1 tabular-nums">{fmtPct(m.approval_rate)}</td>
                          <td className="text-right pl-1 tabular-nums text-slate-700">{fmtMoney(m.submitted_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          {/* Stage Ageing — actual files stuck at each stage.
              Answers "which specific files are slowing us down". */}
          <Panel title="Stage ageing · files currently stuck" icon={Timer} testid="pipeline-stage-ageing">
            {(() => {
              const ageMap = current.stage_ageing || {};
              const flat = [];
              PIPELINE_STAGES.forEach((s) => {
                (ageMap[s.key] || []).forEach((f) => flat.push({ ...f, stage: s.key, stage_label: s.label, stage_tone: s.tone }));
              });
              flat.sort((a, b) => b.days_at_stage - a.days_at_stage);
              const top = flat.slice(0, 12);
              if (top.length === 0) {
                return <p className="text-xs text-slate-400 italic">No open files sitting long enough to flag. Deals move through the pipeline within a day.</p>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left py-1 pr-2">Client</th>
                        <th className="text-left px-2">Stage</th>
                        <th className="text-left px-2">Broker</th>
                        <th className="text-left px-2">Lender</th>
                        <th className="text-left px-2">Credit officer</th>
                        <th className="text-right px-2">Loan $</th>
                        <th className="text-right pl-2">Days here</th>
                      </tr>
                    </thead>
                    <tbody>
                      {top.map((f) => (
                        <tr key={f.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`aged-${f.id}`}>
                          <td className="py-1.5 pr-2 font-bold text-slate-900 truncate max-w-[140px]">{f.client_name}</td>
                          <td className="px-2">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${f.stage_tone}18`, color: f.stage_tone }}>
                              {f.stage_label}
                            </span>
                          </td>
                          <td className="px-2 text-slate-700">{f.broker}</td>
                          <td className="px-2 text-slate-700">{f.lender}</td>
                          <td className="px-2 text-slate-700">{f.credit_team_member}</td>
                          <td className="text-right px-2 tabular-nums text-slate-700">{fmtMoney(f.est_loan_amount)}</td>
                          <td className={`text-right pl-2 tabular-nums font-black ${f.days_at_stage >= 14 ? 'text-rose-600' : f.days_at_stage >= 7 ? 'text-amber-600' : 'text-slate-700'}`}>
                            {f.days_at_stage}d
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
            {/* Broker leaderboard — moved to its own row below the assessor detail */}
            <Panel title="Broker leaderboard" icon={Users} testid="pipeline-brokers">
              {current.brokers.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No broker data.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left py-1">Broker</th>
                        <th className="text-right px-2">Files</th>
                        <th className="text-right px-2">Sub $</th>
                        <th className="text-right px-2">App $</th>
                        <th className="text-right pl-2 text-emerald-700">Set $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.brokers.slice(0, 12).map((b) => (
                        <tr key={b.broker} className="border-b border-slate-50" data-testid={`broker-${b.broker}`}>
                          <td className="py-1.5 font-bold text-slate-900 truncate max-w-[180px]">{b.broker}</td>
                          <td className="text-right px-2 tabular-nums">{b.count}</td>
                          <td className="text-right px-2 tabular-nums text-slate-700">{fmtMoney(b.submitted_amount)}</td>
                          <td className="text-right px-2 tabular-nums text-slate-700">{fmtMoney(b.approved_amount)}</td>
                          <td className="text-right pl-2 tabular-nums font-bold text-emerald-700">{fmtMoney(b.settled_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Distribution breakdowns */}
            {[
              ['Lead source', 'lead_source'],
              ['Loan purpose', 'loan_purpose'],
              ['Application type', 'application_type'],
              ['Loan type', 'loan_type'],
            ].map(([label, key]) => {
              const rows = Object.entries(current.breakdown[key] || {}).sort((a, b) => b[1] - a[1]);
              const total = rows.reduce((n, [, c]) => n + c, 0) || 1;
              return (
                <Panel key={key} title={label} icon={BarChart3} testid={`pipeline-breakdown-${key}`}>
                  {rows.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No data yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {rows.map(([name, cnt]) => (
                        <div key={name}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-slate-700 truncate">{name}</span>
                            <span className="font-black tabular-nums text-slate-900">{cnt}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-0.5 overflow-hidden">
                            <div className="h-full bg-slate-800 rounded-full" style={{ width: `${(cnt / total) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              );
            })}
          </div>

          {/* Reasons — decline / withdrawal / hold */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              ['Top decline reasons',    'decline',    '#ef4444', XCircle],
              ['Top withdrawal reasons', 'withdrawal', '#94a3b8', TrendingDown],
              ['Top hold reasons',       'hold',       '#f97316', PauseCircle],
            ].map(([label, key, tone, IconEl]) => {
              const rows = current.reasons[key] || [];
              return (
                <Panel key={key} title={label} icon={AlertTriangle} testid={`pipeline-reasons-${key}`}>
                  {rows.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No {key} reasons captured yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {rows.map((r) => (
                        <li key={r.reason} className="flex items-center justify-between text-xs">
                          <span className="inline-flex items-center gap-1.5 font-bold text-slate-700">
                            <IconEl className="w-3.5 h-3.5" style={{ color: tone }} /> {r.reason}
                          </span>
                          <span className="font-black tabular-nums text-slate-900">{r.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
              );
            })}
          </div>

          {/* Age brackets + Settlement forecast */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Open pipeline · age brackets" icon={Timer} testid="pipeline-age-brackets">
              {(() => {
                const buckets = current.age_brackets || {};
                const files = current.age_bracket_files || {};
                const order = ['0-3d','4-7d','8-14d','15-30d','30d+'];
                const total = order.reduce((s, k) => s + (buckets[k] || 0), 0);
                if (total === 0) return <p className="text-xs text-slate-400 italic">No open files right now.</p>;
                const tones = { '0-3d':'#10b981', '4-7d':'#eab308', '8-14d':'#f59e0b', '15-30d':'#ef4444', '30d+':'#7c3aed' };
                const toggle = (k) => setOpenBuckets((prev) => ({ ...prev, [k]: !prev[k] }));
                return (
                  <div className="space-y-2">
                    {order.map(k => {
                      const cnt = buckets[k] || 0;
                      const pct = total ? (cnt / total) * 100 : 0;
                      const bucketFiles = files[k] || [];
                      const isOpen = !!openBuckets[k];
                      return (
                        <div key={k} data-testid={`age-${k}`}>
                          <button
                            type="button"
                            onClick={() => cnt > 0 && toggle(k)}
                            disabled={cnt === 0}
                            className={`w-full flex items-center gap-3 rounded-md px-1 py-1 transition-colors ${cnt > 0 ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-60 cursor-default'}`}
                            data-testid={`age-toggle-${k}`}
                          >
                            {cnt > 0 ? (
                              isOpen
                                ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                : <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            ) : (
                              <span className="w-3.5 h-3.5 shrink-0" />
                            )}
                            <div className="w-14 text-xs font-black uppercase tracking-wider text-slate-600 text-left">{k}</div>
                            <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden">
                              <div className="h-full rounded-md flex items-center px-2 text-[11px] font-black text-white transition-all" style={{ width: `${Math.max(4, pct)}%`, background: tones[k] }}>
                                {cnt > 0 && cnt}
                              </div>
                            </div>
                            <div className="w-10 text-right text-xs tabular-nums text-slate-500">{pct.toFixed(0)}%</div>
                          </button>

                          {isOpen && bucketFiles.length > 0 && (
                            <div className="mt-1 mb-2 ml-6 rounded-lg border border-slate-200 bg-slate-50/50 overflow-x-auto" data-testid={`age-files-${k}`}>
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-[9.5px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-white">
                                    <th className="text-left py-1.5 px-2">Client</th>
                                    <th className="text-left px-2">Stage</th>
                                    <th className="text-left px-2">Broker</th>
                                    <th className="text-left px-2">Lender</th>
                                    <th className="text-left px-2">Credit</th>
                                    <th className="text-left px-2">Purpose</th>
                                    <th className="text-right px-2">Loan $</th>
                                    <th className="text-right pr-2">Days open</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bucketFiles.map((f) => (
                                    <tr key={f.id} className="border-b border-slate-100 last:border-0 hover:bg-white" data-testid={`age-file-${f.id}`}>
                                      <td className="py-1.5 px-2 font-bold text-slate-900 truncate max-w-[140px]" title={f.client_name}>{f.client_name}</td>
                                      <td className="px-2">
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] font-bold bg-slate-200 text-slate-700 whitespace-nowrap">
                                          {f.stage_label}
                                        </span>
                                      </td>
                                      <td className="px-2 text-slate-700 truncate max-w-[100px]" title={f.broker}>{f.broker}</td>
                                      <td className="px-2 text-slate-700 truncate max-w-[100px]" title={f.lender}>{f.lender}</td>
                                      <td className="px-2 text-slate-700 truncate max-w-[100px]" title={f.credit_team_member}>{f.credit_team_member}</td>
                                      <td className="px-2 text-slate-700 truncate max-w-[110px]" title={f.loan_purpose}>{f.loan_purpose}</td>
                                      <td className="text-right px-2 tabular-nums text-slate-800 font-bold">{fmtMoney(f.est_loan_amount)}</td>
                                      <td className="text-right pr-2 tabular-nums font-black" style={{ color: tones[k] }}>{f.days_open}d</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <p className="text-[11px] text-slate-400 mt-2">Total open files: <b className="text-slate-700">{total}</b> · click any bar to view the files</p>
                  </div>
                );
              })()}
            </Panel>

            <Panel title="Settlement forecast · next 90 days" icon={CheckCircle2} testid="pipeline-settlement-forecast">
              {(() => {
                const rows = current.settlement_forecast || [];
                if (rows.length === 0) return <p className="text-xs text-slate-400 italic">No projected settlements in the next 90 days.</p>;
                const max = Math.max(...rows.map(r => r.amount));
                return (
                  <div className="space-y-2">
                    {rows.map(r => (
                      <div key={r.month} className="flex items-center gap-3" data-testid={`forecast-${r.month}`}>
                        <div className="w-20 text-xs font-black text-slate-700">{new Date(`${r.month}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</div>
                        <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full rounded-md flex items-center px-2 text-[11px] font-black text-white bg-sky-600" style={{ width: `${max ? (r.amount / max) * 100 : 4}%` }}>
                            {r.count} deal{r.count === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div className="w-24 text-right text-xs tabular-nums font-bold text-emerald-700">{fmtMoney(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Panel>
          </div>

          {/* Purpose × outcome + Broker × Lender matrix */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Loan purpose · outcome" icon={BarChart3} testid="pipeline-purpose-outcome">
              {(current.purpose_outcome || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No data yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        <th className="text-left py-1">Purpose</th>
                        <th className="text-right px-2">Total</th>
                        <th className="text-right px-2 text-slate-600">Open</th>
                        <th className="text-right px-2 text-emerald-700">Approved</th>
                        <th className="text-right px-2 text-sky-700">Settled</th>
                        <th className="text-right px-2 text-rose-600">Declined</th>
                        <th className="text-right pl-2">Appr %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.purpose_outcome.map((p) => (
                        <tr key={p.purpose} className="border-b border-slate-50" data-testid={`po-${p.purpose}`}>
                          <td className="py-1.5 font-bold text-slate-900 truncate max-w-[180px]">{p.purpose}</td>
                          <td className="text-right px-2 tabular-nums">{p.total}</td>
                          <td className="text-right px-2 tabular-nums text-slate-600">{p.open}</td>
                          <td className="text-right px-2 tabular-nums font-bold text-emerald-700">{p.approved}</td>
                          <td className="text-right px-2 tabular-nums text-sky-700">{p.settled}</td>
                          <td className="text-right px-2 tabular-nums text-rose-600">{p.declined}</td>
                          <td className="text-right pl-2 tabular-nums">{fmtPct(p.approval_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title="Broker × Lender matrix" icon={Users} testid="pipeline-broker-lender-matrix">
              {(() => {
                const m = current.broker_lender_matrix || { lenders: [], rows: [] };
                if (m.rows.length === 0) return <p className="text-xs text-slate-400 italic">No data.</p>;
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 border-b border-slate-100">
                          <th className="text-left py-1">Broker</th>
                          {m.lenders.map(l => <th key={l} className="text-right px-2">{l}</th>)}
                          <th className="text-right pl-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.rows.map(r => (
                          <tr key={r.broker} className="border-b border-slate-50" data-testid={`bl-${r.broker}`}>
                            <td className="py-1.5 font-bold text-slate-900 truncate max-w-[140px]">{r.broker}</td>
                            {m.lenders.map(l => (
                              <td key={l} className="text-right px-2 tabular-nums">{(r.counts[l] || 0) || <span className="text-slate-300">—</span>}</td>
                            ))}
                            <td className="text-right pl-2 tabular-nums font-black text-slate-900">{r.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </Panel>
          </div>

          {/* 12-week trend */}
          <Panel title="12-week trend" icon={TrendingUp} testid="pipeline-weekly-trend">
            {(current.weekly_trend || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No data in the last 12 weeks.</p>
            ) : (() => {
              const rows = current.weekly_trend;
              const maxN = Math.max(1, ...rows.flatMap(r => [r.sub, r.appr, r.set, r.dec]));
              return (
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1.5" style={{ minHeight: 110 }}>
                    {rows.map(r => (
                      <div key={r.week} className="flex-1 min-w-[50px] flex flex-col items-center gap-0.5" data-testid={`wk-${r.week}`}>
                        <div className="w-full h-24 flex items-end gap-0.5">
                          <div className="flex-1 bg-slate-500 rounded-t" style={{ height: `${(r.sub / maxN) * 100}%` }} title={`Submitted: ${r.sub}`} />
                          <div className="flex-1 bg-emerald-500 rounded-t" style={{ height: `${(r.appr / maxN) * 100}%` }} title={`Approved: ${r.appr}`} />
                          <div className="flex-1 bg-sky-500 rounded-t" style={{ height: `${(r.set / maxN) * 100}%` }} title={`Settled: ${r.set}`} />
                          <div className="flex-1 bg-rose-500 rounded-t" style={{ height: `${(r.dec / maxN) * 100}%` }} title={`Declined: ${r.dec}`} />
                        </div>
                        <div className="text-[9px] font-bold text-slate-500 tabular-nums whitespace-nowrap">{r.week.slice(-3)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-[10px] font-bold uppercase tracking-wider">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-slate-500 rounded-sm"/>Submitted</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm"/>Approved</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-sky-500 rounded-sm"/>Settled</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 rounded-sm"/>Declined</span>
                  </div>
                </div>
              );
            })()}
          </Panel>
        </>
      )}
    </motion.div>
  );
};

export default PipelineCommandCentre;
